/**
 * RoomSession (PPL-33) — three-voice shared mediation room on web.
 *
 * Architecture:
 *  - `useRoomSession` hook owns all subscriptions and callables.
 *  - Realtime `onSnapshot` on `mediations/{id}/roomMessages` — NOT polling.
 *  - Human turns via `sendMediationMessageEurW`.
 *  - Mediator turn via `requestMediatorTurnEurW` (20 s cooldown with counter).
 *  - Mark seen via `markMediationRoomSeenEurW` on focus + scroll-to-bottom.
 *  - "Step aside" → `/rooms/:id/prep` (RoomPrep, PPL-30).
 *
 * Three distinct author treatments:
 *  1. OWN human turn   — right-aligned gradient pill (f8-user-bubble pattern).
 *  2. OTHER human turn — left-aligned muted surface pill + sender tag.
 *  3. f8 turn          — gradient-border pill + small orb glyph. NO avatar
 *                        (PPL-39: f8 is a third presence, never a human slot).
 *
 * Moderation errors from `sendMediationMessageEurW` surface as an inline error
 * banner beneath the header; the typed text is restored so the user doesn't
 * lose it.
 *
 * CSS borrows the `.f8-chat-shell` shell + composer + bubble patterns from
 * `F8Chat.css` and supplements with `RoomSession.css` for the three-voice
 * treatments.
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { Archive, ArrowUp, Camera, CheckSquare, CornerUpLeft, Image as ImageIcon, LogOut, Plus, Smile, Zap } from 'react-feather';
import { useAuth } from '../hooks/useAuth';
import useRoomSession from '../hooks/useRoomSession';
import {
  MESSAGE_LIMIT,
  declineRoomPictureRequest,
  getMediationCounterpart,
  reactToRoomMessage,
  requestRoomPicture,
  resumeRoom,
  sendRoomImage,
} from '../services/roomService';
import type { RoomMessage } from '../types/room';
import { ALLOWED_EMOJIS } from '../types/social';
import type { TimestampLike } from '../types/pplTalk';
import type { FirebaseTimestamp } from '../types/pplTalk';
import '../styles/F8Chat.css';
import '../styles/RoomSession.css';

const MAX_INPUT_LENGTH = 600;

// ─── Timestamp helper ────────────────────────────────────────────────────────

function fmtTime(value: TimestampLike): string {
  if (!value) return '';
  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'number') {
    date = new Date(value);
  } else if (typeof value === 'string') {
    date = new Date(value);
  } else {
    const ts = value as FirebaseTimestamp;
    date = typeof ts.toDate === 'function' ? ts.toDate!() : new Date(ts.seconds * 1000);
  }
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface BubbleProps {
  message: RoomMessage;
  currentUid: string;
  otherName: string;
  /** Reactions (copied from Conversation.tsx): picker open state + handlers. */
  pickerOpen: boolean;
  onTogglePicker: (messageId: string) => void;
  onReact: (messageId: string, emoji: string) => void;
  /** Pictures (PPL-186/187). */
  onOpenImage: (url: string) => void;
  onShareForRequest: (requestMessageId: string) => void;
  /** Resolves a request: decline (other side) or withdraw (requester) — same callable. */
  onDeclineRequest: (requestMessageId: string) => void;
  requestBusy: boolean;
  /** Room state gate — card actions only render while 'ongoing'. */
  roomOpen: boolean;
}

/** Image body rendered inside a bubble (PPL-186). */
function ImageBody({ message, onOpenImage }: { message: RoomMessage; onOpenImage: (url: string) => void }) {
  if (!message.imageUrl) return null;
  return (
    <>
      <img
        src={message.imageUrl}
        alt={message.text || 'shared photo'}
        style={{
          display: 'block',
          maxWidth: '100%',
          maxHeight: 320,
          borderRadius: 12,
          cursor: 'zoom-in',
        }}
        onClick={() => onOpenImage(message.imageUrl as string)}
      />
      {message.text ? <div style={{ marginTop: 6 }}>{message.text}</div> : null}
    </>
  );
}

/**
 * picture_request card (PPL-187) — the request-a-picture skill as the receiver
 * sees it (actionable while open) and as the requester sees it (status only).
 * Styled after the f8 card treatment; status updates arrive via onSnapshot.
 */
function PictureRequestCard({
  message,
  mine,
  otherName,
  roomOpen,
  onShare,
  onDecline,
  onWithdraw,
  busy,
}: {
  message: RoomMessage;
  mine: boolean;
  otherName: string;
  /** Card actions only work while the room is live — hidden when paused/ended. */
  roomOpen: boolean;
  onShare: () => void;
  onDecline: () => void;
  onWithdraw: () => void;
  busy: boolean;
}) {
  const status = message.requestStatus ?? 'open';
  const statusLine =
    status === 'open'
      ? (mine ? `waiting for ${otherName}…` : null)
      : status === 'fulfilled'
        ? 'picture shared ✓'
        : status === 'cancelled'
          ? (mine ? 'you withdrew this request' : 'request withdrawn')
          : (mine ? `${otherName} passed for now` : 'you passed for now');
  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: 14,
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
        maxWidth: 320,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Camera size={15} style={{ flexShrink: 0, color: '#d8b4fe' }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
          {mine ? 'you asked for a picture' : `${otherName} asked you for a picture`}
        </span>
      </div>
      {statusLine && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'rgba(232,220,250,0.6)' }}>{statusLine}</div>
      )}
      {!mine && status === 'open' && roomOpen && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button
            type="button"
            onClick={onShare}
            disabled={busy}
            style={{
              appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', border: 0,
              flex: 1, padding: '8px 10px', borderRadius: 10,
              background: 'linear-gradient(135deg, #d946ef, #a855f7)',
              color: '#fff', fontSize: 12.5, fontWeight: 700,
              opacity: busy ? 0.6 : 1,
            }}
          >
            share a picture
          </button>
          <button
            type="button"
            onClick={onDecline}
            disabled={busy}
            style={{
              appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
              flex: 1, padding: '8px 10px', borderRadius: 10,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(240,232,250,0.85)', fontSize: 12.5, fontWeight: 600,
              opacity: busy ? 0.6 : 1,
            }}
          >
            not now
          </button>
        </div>
      )}
      {mine && status === 'open' && roomOpen && (
        <button
          type="button"
          onClick={onWithdraw}
          disabled={busy}
          style={{
            appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
            marginTop: 8, padding: '5px 0', border: 0, background: 'none',
            color: 'rgba(232,220,250,0.5)', fontSize: 11.5, fontWeight: 600,
            textDecoration: 'underline', textUnderlineOffset: 3,
            opacity: busy ? 0.6 : 1,
          }}
        >
          withdraw request
        </button>
      )}
    </div>
  );
}

/** Reaction chips under a bubble — same treatment as Conversation.tsx. */
function ReactionChips({ message, alignEnd }: { message: RoomMessage; alignEnd: boolean }) {
  if (!message.reactions || message.reactions.length === 0) return null;
  return (
    <div style={{
      display: 'flex',
      gap: 4,
      marginTop: 2,
      justifyContent: alignEnd ? 'flex-end' : 'flex-start',
    }}>
      {message.reactions.map((r, i) => (
        <span key={i} style={{
          fontSize: 14,
          padding: '2px 6px',
          borderRadius: 999,
          background: 'rgba(255,255,255,0.06)',
        }}>
          {r.emoji}
        </span>
      ))}
    </div>
  );
}

function MessageBubble({
  message,
  currentUid,
  otherName,
  pickerOpen,
  onTogglePicker,
  onReact,
  onOpenImage,
  onShareForRequest,
  onDeclineRequest,
  requestBusy,
  roomOpen,
}: BubbleProps) {
  const messageType = message.messageType ?? 'text';
  const isF8 = message.senderId === 'f8' || message.isArtificial === true;
  const isOwn = !isF8 && message.senderId === currentUid;
  const isOther = !isF8 && !isOwn;

  // Server-written check-in nudge: render as centred system note.
  // The server may tag these via a separate field in future; for now we detect
  // via author='f8' + short, bracket-wrapped copy is not guaranteed — so we
  // don't special-case by content. Check-in nudges are surfaced at call-site
  // via an `isCheckinNudge` flag if the caller passes it; here we default to
  // the normal f8 treatment.

  if (isF8) {
    return (
      <div className="med-room-f8-wrap" key={message.id}>
        <div className="med-room-f8-tag-row">
          <span className="med-room-f8-tag-orb" aria-hidden="true" />
          <span className="med-room-f8-tag">f8</span>
        </div>
        <div className="med-room-f8-bubble-border">
          <div className="med-room-f8-bubble">{message.text}</div>
        </div>
        <span className="med-room-ts">{fmtTime(message.createdAt)}</span>
      </div>
    );
  }

  if (isOwn) {
    return (
      <div className="med-room-own-wrap" key={message.id}>
        {messageType === 'picture_request' ? (
          <PictureRequestCard
            message={message}
            mine
            otherName={otherName}
            roomOpen={roomOpen}
            onShare={() => {}}
            onDecline={() => {}}
            onWithdraw={() => onDeclineRequest(message.id)}
            busy={requestBusy}
          />
        ) : (
          <div className="med-room-own-bubble" style={messageType === 'image' ? { padding: 6 } : undefined}>
            {messageType === 'image' ? <ImageBody message={message} onOpenImage={onOpenImage} /> : message.text}
          </div>
        )}
        {/* The other side's reaction to my message arrives via the same
            onSnapshot — render it exactly like 1:1 chat does on own bubbles. */}
        <ReactionChips message={message} alignEnd />
        <span className="med-room-ts">{fmtTime(message.createdAt)}</span>
      </div>
    );
  }

  if (isOther) {
    if (messageType === 'picture_request') {
      return (
        <div className="med-room-other-wrap" key={message.id}>
          <span className="med-room-other-tag">{otherName}</span>
          <PictureRequestCard
            message={message}
            mine={false}
            otherName={otherName}
            roomOpen={roomOpen}
            onShare={() => onShareForRequest(message.id)}
            onDecline={() => onDeclineRequest(message.id)}
            onWithdraw={() => {}}
            busy={requestBusy}
          />
          <span className="med-room-ts">{fmtTime(message.createdAt)}</span>
        </div>
      );
    }
    return (
      <div className="med-room-other-wrap" key={message.id}>
        <span className="med-room-other-tag">{otherName}</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', position: 'relative' }}>
          <div className="med-room-other-bubble" style={messageType === 'image' ? { padding: 6 } : undefined}>
            {messageType === 'image' ? <ImageBody message={message} onOpenImage={onOpenImage} /> : message.text}
          </div>
          {/* Reaction trigger — other person's messages only (mirrors 1:1's !isMine) */}
          <button
            onClick={() => onTogglePicker(message.id)}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(240,232,250,0.85)',
              cursor: 'pointer',
              opacity: 0.75,
              transition: 'opacity 0.15s',
              padding: 4,
              flexShrink: 0,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.75')}
            aria-label="react to this message"
          >
            <Smile size={14} />
          </button>
          {/* Reaction picker — absolutely positioned above the bubble so opening
              it never shifts the message flow or the scroll position. */}
          {pickerOpen && (
            <div style={{
              position: 'absolute',
              bottom: 'calc(100% + 4px)',
              left: 0,
              zIndex: 30,
              display: 'flex',
              gap: 4,
              padding: '6px 10px',
              borderRadius: 12,
              background: 'rgba(28,16,42,0.96)',
              border: '1px solid rgba(255,255,255,0.1)',
              backdropFilter: 'blur(16px)',
              width: 'max-content',
              boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
            }}>
              {ALLOWED_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => onReact(message.id, emoji)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    padding: '2px 4px',
                    borderRadius: 6,
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
        <ReactionChips message={message} alignEnd={false} />
        <span className="med-room-ts">{fmtTime(message.createdAt)}</span>
      </div>
    );
  }

  return null;
}

// ─── Participant avatar helper ───────────────────────────────────────────────

interface ParticipantAvatarProps {
  name?: string | null;
  photoURL?: string | null;
  /** Mirror the message colour scheme: own = gradient bubble, other = muted surface. */
  isOwn?: boolean;
}

function ParticipantAvatar({ name, photoURL, isOwn }: ParticipantAvatarProps) {
  const initial = (name?.[0] ?? '?').toUpperCase();
  const tone = isOwn ? ' med-room-avatar--own' : ' med-room-avatar--other';
  if (photoURL) {
    return (
      <img
        src={photoURL}
        alt={name ?? 'participant'}
        className={`med-room-avatar${tone}`}
      />
    );
  }
  return (
    <div className={`med-room-avatar-fallback${tone}`} aria-label={name ?? 'participant'}>
      {initial}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

/** States where the step-aside affordance is visible. */
const STEP_ASIDE_STATES = new Set(['joint', 'ongoing', 'paused']);

const RoomSession: React.FC = () => {
  const { mediationId } = useParams<{ mediationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  const {
    mediation,
    messages,
    side,
    isSending,
    isRequestingTurn,
    initialLoadComplete,
    error,
    mediatorCooldownRemaining,
    atLimit,
    sendMessage,
    requestTurn,
    markSeen,
  } = useRoomSession(mediationId, uid);

  const [inputText, setInputText] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [reactionPickerMsg, setReactionPickerMsg] = useState<string | null>(null);
  // Pictures (PPL-186/187)
  const [attachOpen, setAttachOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The open picture request an upcoming upload fulfils (set by the card's
  // "share a picture" action; null for a plain attach).
  const fulfillsRef = useRef<string | null>(null);

  // The send/moderation notice is transient — pop it in, then clear it after 5s.
  // (Load `error` is separate and stays put.)
  useEffect(() => {
    if (!sendError) return undefined;
    const t = window.setTimeout(() => setSendError(null), 5000);
    return () => window.clearTimeout(t);
  }, [sendError]);
  const [otherFirstName, setOtherFirstName] = useState<string | null>(null);
  const [otherHandle, setOtherHandle] = useState<string | null>(null);
  const otherProfileFetchedRef = useRef(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const canSend = inputText.trim().length > 0 && !isSending;
  const askF8Disabled = isRequestingTurn || mediatorCooldownRemaining > 0;

  // Scroll to bottom on new messages.
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, isSending]);

  // Mark seen on mount (focus) and on scroll-to-bottom.
  useEffect(() => {
    markSeen();
  }, [markSeen]);

  // The room opener is now seeded SERVER-SIDE the moment the room opens
  // (setMediationReadyEurW → seedRoomOpener), which fixed the fragile PPL-155
  // client auto-intro: it only fired from the inviter's web browser, so app
  // users and invitee-first arrivals landed on a blank transcript.

  const handleScrollEnd = useCallback(() => {
    if (!messagesRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      markSeen();
    }
  }, [markSeen]);

  // Derived participant info from the root doc (or fall through to placeholder).
  const inviterUid = mediation?.inviterUid ?? null;
  const inviteeUid = mediation?.inviteeUid ?? null;

  const otherUid = uid === inviterUid ? inviteeUid : inviterUid;

  // Resolve the other participant's display name via the mediation-scoped
  // resolver (getPublicUserProfileEurW is handle-only and never returns
  // firstName). One fetch per session — cached in component state.
  useEffect(() => {
    if (!mediationId || otherProfileFetchedRef.current) return;
    otherProfileFetchedRef.current = true;
    void (async () => {
      try {
        const { counterpart } = await getMediationCounterpart(mediationId);
        if (counterpart.firstName) setOtherFirstName(counterpart.firstName);
        if (counterpart.tetherHandle) setOtherHandle(counterpart.tetherHandle);
      } catch {
        // non-fatal — fall back to generic label
      }
    })();
  }, [mediationId]);

  // My own display name from the current user session.
  const myFirstName = (user as any)?.firstName ?? null;

  // Resolved display labels.
  const myLabel = myFirstName ?? (side === 'inviter' ? 'you (inviter)' : side === 'invitee' ? 'you (invitee)' : 'you');
  const otherLabel = otherFirstName ?? 'them';

  // Step-aside is visible when the mediation state is joint/ongoing/paused.
  const canStepAside =
    mediation?.state != null && STEP_ASIDE_STATES.has(mediation.state);

  // The other side checked in and opened a round — you're being prompted to
  // check in too. Set === you means you're the one waiting, so no nudge.
  const checkinRequested =
    !!mediation?.checkinRoundStarter && mediation.checkinRoundStarter !== uid;

  const isPaused = mediation?.state === 'paused';
  // PPL-234 v2 — ended mirrors paused as an IN-ROOM affordance instead of the
  // silent auto-redirect this page briefly shipped: the state flip arrives live
  // over the same subscription that delivers pause, and a visible "this room
  // has ended" bar with a summary CTA beats teleporting someone mid-read.
  // Covers every ending path (keep-summary, keep-nothing, block-closure): all
  // of them transition the doc to state 'ended' — deleteForBoth never deletes.
  const isEnded = mediation?.state === 'ended';
  const [resuming, setResuming] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  };

  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text) return;
    const prev = inputText;
    setInputText('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setSendError(null);
    try {
      await sendMessage(text);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "couldn't send";
      setSendError(msg);
      setInputText(prev); // restore so the user doesn't lose what they typed
    }
  }, [inputText, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleAskF8 = useCallback(async () => {
    if (askF8Disabled) return;
    try {
      await requestTurn();
    } catch {
      // Transient — show the auto-dismissing notice (requestTurn no longer sets
      // the persistent load error, which would leave an always-on banner).
      setSendError("couldn't reach f8 just now — try again in a moment.");
    }
  }, [askF8Disabled, requestTurn]);

  // Copied from Conversation.tsx handleReaction — server enforces the allowlist
  // and one-reaction-per-user; the update lands via the live subscription.
  const handleReaction = useCallback(async (messageId: string, emoji: string) => {
    if (!mediationId) return;
    try {
      await reactToRoomMessage(mediationId, messageId, emoji);
    } catch (err) {
      console.error('Reaction error:', err);
    }
    setReactionPickerMsg(null);
  }, [mediationId]);

  const handleTogglePicker = useCallback((messageId: string) => {
    setReactionPickerMsg((cur) => (cur === messageId ? null : messageId));
  }, []);

  // ── Pictures (PPL-186/187) ─────────────────────────────────────────────────

  const openFilePicker = useCallback((fulfillsRequestId: string | null) => {
    fulfillsRef.current = fulfillsRequestId;
    setAttachOpen(false);
    fileInputRef.current?.click();
  }, []);

  const handleFileChosen = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file || !mediationId || !uid || uploading) return;
    const fulfills = fulfillsRef.current;
    fulfillsRef.current = null;
    setUploading(true);
    setSendError(null);
    try {
      const result = await sendRoomImage(mediationId, uid, file, fulfills ? { fulfillsRequestId: fulfills } : {});
      if (!result.success) {
        setSendError(result.reason || "that picture couldn't be shared.");
      }
    } catch (err) {
      setSendError(err instanceof Error ? err.message : "couldn't upload the picture.");
    } finally {
      setUploading(false);
    }
  }, [mediationId, uid, uploading]);

  const handleRequestPicture = useCallback(async () => {
    if (!mediationId || requestBusy) return;
    setAttachOpen(false);
    setRequestBusy(true);
    setSendError(null);
    try {
      await requestRoomPicture(mediationId);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : "couldn't send the picture request.");
    } finally {
      setRequestBusy(false);
    }
  }, [mediationId, requestBusy]);

  const handleDeclineRequest = useCallback(async (requestMessageId: string) => {
    if (!mediationId || requestBusy) return;
    setRequestBusy(true);
    try {
      await declineRoomPictureRequest(mediationId, requestMessageId);
    } catch (err) {
      // Status stays open — surface the failure so the card's silence doesn't
      // read as success; it remains actionable for a retry.
      setSendError(err instanceof Error ? err.message : "couldn't update the picture request.");
    } finally {
      setRequestBusy(false);
    }
  }, [mediationId, requestBusy]);

  const handleStepAside = () => {
    if (mediationId) {
      // A private moment with f8 that returns to the room — NOT the prep→consent
      // flow (which would make you re-do sharing rules mid-conversation).
      navigate(`/rooms/${mediationId}/aside`);
    }
  };

  const handleCheckin = () => {
    if (mediationId) {
      navigate(`/rooms/${mediationId}/checkin`);
    }
  };

  const handleEnd = () => {
    if (mediationId) {
      navigate(`/rooms/${mediationId}/end`);
    }
  };

  const handleResume = useCallback(async () => {
    if (!mediationId || resuming) return;
    setResuming(true);
    try {
      await resumeRoom(mediationId);
      // The room doc flips to 'ongoing' via the live subscription; f8 also posts
      // an "open again" note, so no further UI work here.
    } catch {
      /* surfaced by the subscription / next attempt */
    } finally {
      setResuming(false);
    }
  }, [mediationId, resuming]);

  // ── Fallback states ────────────────────────────────────────────────────────

  if (!mediationId) {
    return (
      <div className="med-room-page">
        <div className="med-room-fallback">room not found.</div>
      </div>
    );
  }

  if (!uid) {
    return (
      <div className="med-room-page">
        <div className="med-room-fallback">sign in to open this room.</div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="med-room-page f8-chat-shell">

      {/* Header: two avatars + f8 orb + step-aside. No back button — the room is
          forward-only; you can't navigate back into prep once it's open (PPL-161). */}
      <div className="med-room-header">
        <div className="med-room-presence-row" role="group" aria-label="room participants">
          {/* Inviter */}
          <div className="med-room-participant">
            <ParticipantAvatar name={side === 'inviter' ? myLabel : otherLabel} photoURL={null} isOwn={side === 'inviter'} />
            <span className="med-room-participant-name">
              {side === 'inviter' ? 'you' : 'them'}
            </span>
          </div>

          {/* f8 orb — the third presence, NOT an avatar (PPL-39) */}
          <div className="med-room-orb-slot" aria-label="f8 mediator">
            <div className="med-room-orb" aria-hidden="true" />
            <span className="med-room-orb-label">f8</span>
          </div>

          {/* Invitee */}
          <div className="med-room-participant">
            <ParticipantAvatar name={side === 'invitee' ? myLabel : otherLabel} photoURL={null} isOwn={side === 'invitee'} />
            <span className="med-room-participant-name">
              {side === 'invitee' ? 'you' : 'them'}
            </span>
          </div>
        </div>

        {/* Step-aside + check-in + end affordances — visible during active states */}
        {canStepAside && (
          <div className="med-room-header-actions">
            <button
              type="button"
              className="med-room-step-aside-btn"
              onClick={handleStepAside}
              aria-label="step aside and talk to f8 privately"
            >
              <CornerUpLeft size={13} />
              <span>step aside</span>
            </button>
            <button
              type="button"
              className="med-room-step-aside-btn"
              onClick={handleCheckin}
              aria-label="check in on how this is going"
            >
              <CheckSquare size={13} />
              <span>check in</span>
            </button>
            <button
              type="button"
              className="med-room-step-aside-btn med-room-end-btn"
              onClick={handleEnd}
              aria-label="end this room"
            >
              <LogOut size={13} />
              <span>end</span>
            </button>
          </div>
        )}
      </div>

      {/* Error banner (send moderation / load errors) */}
      {(error || sendError) && (
        <div className="med-room-error" role="alert">
          {sendError ?? error}
        </div>
      )}

      {/* Check-in nudge — the other side checked in and it's your turn too */}
      {checkinRequested && (
        <button
          type="button"
          className="med-room-checkin-nudge"
          onClick={handleCheckin}
          aria-label="your turn to check in"
        >
          <CheckSquare size={15} aria-hidden="true" />
          <span className="med-room-checkin-nudge-text">
            <b>your turn to check in</b> — {otherLabel} just checked in on how this is going
          </span>
          <span className="med-room-checkin-nudge-cta">check in</span>
        </button>
      )}

      {/* Messages */}
      <div
        className="f8-messages med-room-messages"
        ref={messagesRef}
        onScroll={handleScrollEnd}
      >
        {!initialLoadComplete ? (
          <div className="med-room-messages-inner">
            <div className="med-room-loading">
              <div className="med-room-loading-border">
                <div className="med-room-loading-inner">
                  <div className="med-room-loading-dots">
                    <span /><span /><span />
                  </div>
                  <span className="med-room-loading-label">loading</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="med-room-messages-inner">
            {atLimit && (
              <div className="med-room-cap-banner" role="note">
                <Archive size={13} aria-hidden="true" />
                <span>
                  {`showing the last ${MESSAGE_LIMIT} messages — earlier conversation has been archived`}
                </span>
              </div>
            )}
            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                message={m}
                currentUid={uid}
                otherName={otherLabel}
                pickerOpen={reactionPickerMsg === m.id}
                onTogglePicker={handleTogglePicker}
                onReact={(messageId, emoji) => void handleReaction(messageId, emoji)}
                onOpenImage={setLightboxUrl}
                onShareForRequest={openFilePicker}
                onDeclineRequest={(id) => void handleDeclineRequest(id)}
                requestBusy={requestBusy || uploading}
                roomOpen={mediation?.state === 'ongoing'}
              />
            ))}

            {/* Uploading indicator (photo turn in flight) */}
            {uploading && (
              <div className="med-room-loading">
                <div className="med-room-loading-border">
                  <div className="med-room-loading-inner">
                    <div className="med-room-loading-dots">
                      <span /><span /><span />
                    </div>
                    <span className="med-room-loading-label">sending photo</span>
                  </div>
                </div>
              </div>
            )}

            {/* Sending indicator */}
            {isSending && (
              <div className="med-room-loading">
                <div className="med-room-loading-border">
                  <div className="med-room-loading-inner">
                    <div className="med-room-loading-dots">
                      <span /><span /><span />
                    </div>
                    <span className="med-room-loading-label">sending</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* "Ask f8" mediator-turn affordance — pointless in an ended room. */}
      {!isEnded && (
      <div className="med-room-ask-f8-row">
        <button
          type="button"
          className="med-room-ask-f8-btn"
          onClick={() => void handleAskF8()}
          disabled={askF8Disabled}
          aria-label={
            mediatorCooldownRemaining > 0
              ? `ask f8 — available in ${mediatorCooldownRemaining}s`
              : 'ask f8 to weigh in'
          }
        >
          <span className="med-room-ask-f8-orb" aria-hidden="true" />
          <Zap size={13} className="med-room-ask-f8-icon" />
          <span>
            {mediatorCooldownRemaining > 0
              ? `ask f8 · ${mediatorCooldownRemaining}s`
              : 'ask f8'}
          </span>
        </button>
      </div>
      )}

      {/* Ended: same live affordance as paused (PPL-234) — the other side
          learns in place that the room is over, with the summary one tap
          away, instead of being silently teleported. */}
      {isEnded ? (
        <div className="f8-composer-area med-room-composer-area">
          <div className="med-room-paused-bar">
            <span className="med-room-paused-text">this room has ended</span>
            <button
              type="button"
              className="med-room-resume-btn"
              onClick={() => navigate(`/rooms/${mediationId}/summary`, { replace: true })}
            >
              see summary
            </button>
          </div>
        </div>
      ) : isPaused ? (
        <div className="f8-composer-area med-room-composer-area">
          <div className="med-room-paused-bar">
            <span className="med-room-paused-text">this room is paused</span>
            <button
              type="button"
              className="med-room-resume-btn"
              onClick={() => void handleResume()}
              disabled={resuming}
            >
              {resuming ? 'resuming…' : 'resume room'}
            </button>
          </div>
        </div>
      ) : (
      /* Composer — text-only, moderated */
      <div className="f8-composer-area med-room-composer-area">
        <div className={`f8-composer-border ${canSend ? 'active' : ''}`}>
          <div className="f8-composer-inner">
            <div className="f8-composer-top">
              {/* "+" attachment menu (PPL-186/187) — overlay popover, never
                  inline in the message column. */}
              <div style={{ position: 'relative', flexShrink: 0, alignSelf: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setAttachOpen((o) => !o)}
                  disabled={uploading}
                  aria-label="add to the conversation"
                  aria-expanded={attachOpen}
                  style={{
                    appearance: 'none', cursor: 'pointer', border: 0,
                    width: 30, height: 30, borderRadius: 9,
                    background: attachOpen ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)',
                    color: 'rgba(240,232,250,0.85)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'transform 0.15s, background 0.15s',
                    transform: attachOpen ? 'rotate(45deg)' : 'none',
                  }}
                >
                  <Plus size={16} />
                </button>
                {attachOpen && (
                  <div
                    role="menu"
                    style={{
                      position: 'absolute', bottom: 'calc(100% + 8px)', left: 0, zIndex: 40,
                      minWidth: 200, padding: 6, borderRadius: 14,
                      background: 'rgba(24,14,32,0.98)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
                    }}
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => openFilePicker(null)}
                      style={{
                        appearance: 'none', cursor: 'pointer', border: 0, background: 'none',
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        padding: '9px 10px', borderRadius: 10, fontFamily: 'inherit',
                        color: '#fff', fontSize: 13, fontWeight: 600, textAlign: 'left',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.07)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                    >
                      <ImageIcon size={15} style={{ color: '#d8b4fe' }} />
                      add a picture
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => void handleRequestPicture()}
                      disabled={requestBusy}
                      style={{
                        appearance: 'none', cursor: 'pointer', border: 0, background: 'none',
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        padding: '9px 10px', borderRadius: 10, fontFamily: 'inherit',
                        color: '#fff', fontSize: 13, fontWeight: 600, textAlign: 'left',
                        opacity: requestBusy ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.07)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                    >
                      <Camera size={15} style={{ color: '#d8b4fe' }} />
                      {requestBusy ? 'asking…' : `request a picture`}
                    </button>
                  </div>
                )}
              </div>
              <textarea
                ref={inputRef}
                className="f8-textarea"
                placeholder="say something…"
                value={inputText}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                rows={1}
                maxLength={MAX_INPUT_LENGTH}
                disabled={isSending}
                aria-label="message"
              />
              <span className="f8-char-count">
                {inputText.length}/{MAX_INPUT_LENGTH}
              </span>
            </div>
            <div className="f8-composer-bottom">
              <div className="f8-composer-right">
                <span className="f8-hint">
                  room · moderated ·{' '}
                  <kbd className="f8-kbd">↵</kbd> to send
                </span>
                <button
                  type="button"
                  className={`f8-send-btn ${canSend ? 'active' : ''}`}
                  onClick={() => void handleSend()}
                  disabled={!canSend}
                  aria-label="send"
                >
                  <ArrowUp size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Hidden file input — the single entry point for both "add a picture"
          and a card's "share a picture" (fulfillsRef distinguishes them). */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => void handleFileChosen(e)}
      />

      {/* Full-size image lightbox — PORTALED to document.body: the app shell
          creates a stacking context that traps fixed overlays under the left
          nav rail on desktop; the portal escapes it and covers the true
          viewport. */}
      {lightboxUrl && createPortal(
        <div
          role="dialog"
          aria-label="photo"
          onClick={() => setLightboxUrl(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(6,3,10,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, cursor: 'zoom-out',
          }}
        >
          <img
            src={lightboxUrl}
            alt="shared photo"
            style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 12 }}
          />
        </div>,
        document.body,
      )}
    </div>
  );
};

export default RoomSession;
